import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

// Reusable cascading address: country -> state -> city + zip (format-validated).
// value: {line1,line2,country,country_code,state,state_id,city,zip}
export default function GeoAddress({ value = {}, onChange, prefix = "addr", showLines = true, compact = false }) {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [zipEx, setZipEx] = useState("");
  const [zipOk, setZipOk] = useState(true);

  useEffect(() => { api.get("/geo/countries").then((r) => setCountries(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (value.country_code) {
      api.get(`/geo/states?country=${value.country_code}`).then((r) => setStates(r.data)).catch(() => setStates([]));
      api.get(`/geo/zip-pattern?country=${value.country_code}`).then((r) => setZipEx(r.data.example || "")).catch(() => {});
    } else { setStates([]); }
    // eslint-disable-next-line
  }, [value.country_code]);

  useEffect(() => {
    if (value.state_id) api.get(`/geo/cities?state_id=${value.state_id}`).then((r) => setCities(r.data)).catch(() => setCities([]));
    else setCities([]);
    // eslint-disable-next-line
  }, [value.state_id]);

  const set = (patch) => onChange({ ...value, ...patch });

  const onCountry = (iso2) => {
    const c = countries.find((x) => x.iso2 === iso2);
    set({ country_code: iso2, country: c?.name || "", state: "", state_id: "", city: "", zip: "" });
  };
  const onState = (sid) => {
    const s = states.find((x) => String(x.id) === String(sid));
    set({ state_id: sid, state: s?.name || "", city: "" });
  };
  const onZip = (v) => {
    set({ zip: v });
    if (!v) { setZipOk(true); return; }
    api.get(`/geo/zip-validate?country=${value.country_code || ""}&value=${encodeURIComponent(v)}`).then((r) => setZipOk(r.data.valid)).catch(() => setZipOk(true));
  };

  const inputCls = "h-11";
  const selCls = "h-11 w-full rounded-md border border-input bg-background px-3 text-sm";

  return (
    <div className="space-y-3">
      {showLines && (
        <div className="grid grid-cols-1 gap-3">
          <Input value={value.line1 || ""} onChange={(e) => set({ line1: e.target.value })} placeholder="Address line 1" className={inputCls} data-testid={`${prefix}-line1`} />
          <Input value={value.line2 || ""} onChange={(e) => set({ line2: e.target.value })} placeholder="Address line 2 (optional)" className={inputCls} data-testid={`${prefix}-line2`} />
        </div>
      )}
      <div className={`grid ${compact ? "grid-cols-1" : "grid-cols-2"} gap-3`}>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Country</Label>
          <select value={value.country_code || ""} onChange={(e) => onCountry(e.target.value)} className={selCls} data-testid={`${prefix}-country`}>
            <option value="">Select country</option>
            {countries.map((c) => <option key={c.iso2} value={c.iso2}>{c.emoji} {c.name}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">State / Province</Label>
          <select value={value.state_id || ""} onChange={(e) => onState(e.target.value)} className={selCls} disabled={!states.length} data-testid={`${prefix}-state`}>
            <option value="">{states.length ? "Select state" : "—"}</option>
            {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>
      <div className={`grid ${compact ? "grid-cols-1" : "grid-cols-2"} gap-3`}>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">City</Label>
          <Input list={`${prefix}-cities`} value={value.city || ""} onChange={(e) => set({ city: e.target.value })} placeholder={cities.length ? "Select or type city" : "Type city"} className={inputCls} data-testid={`${prefix}-city`} />
          <datalist id={`${prefix}-cities`}>{cities.map((c) => <option key={c.id} value={c.name} />)}</datalist>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">ZIP / Postal code</Label>
          <Input value={value.zip || ""} onChange={(e) => onZip(e.target.value)} placeholder={zipEx ? `e.g. ${zipEx}` : "ZIP / PIN"} className={`${inputCls} ${!zipOk ? "border-red-400" : ""}`} data-testid={`${prefix}-zip`} />
          {!zipOk && <p className="text-xs text-red-500" data-testid={`${prefix}-zip-err`}>Invalid format{zipEx ? ` (e.g. ${zipEx})` : ""}</p>}
        </div>
      </div>
    </div>
  );
}
